import React, { useMemo } from 'react'

import {
  AnnotationQueryVariablesProvider,
  RouteParams,
  getVersionRenderStatus,
} from '@sketch/modules-common'
import { RouteComponentProps } from 'react-router-dom'

import { DocumentSidebarLayoutExtraProps } from '../../../components/DocumentSidebarLayout'
import {
  isUpgradeToLatestNeeded,
  UpgradeToLatestVersion,
  useVersioning,
} from '../../../../versioning'
import { useGetVersionQueryWithPolling } from '../../../operations'
import { CanvasAnnotationOverlayContext } from '../../../../canvas/components/CanvasAnnotationOverlayContext'
import { FrameInspectorProvider } from '../../../FrameView/components/FrameInspectorProvider'
import {
  CanvasInteractionsProvider,
  CanvasSelectionProvider,
} from '@sketch/canvas'
import { CanvasLeftPanelResizingProvider } from '../../../../canvas/components/CanvasLeftPanel'
import { GridsAndLayoutsProvider } from '../../../components/GridsAndLayoutsProvider'
import { SectionView } from '../SectionView'
import { CanvasScopeProvider } from '../../../../canvas/scope'

type SectionQueriesRouteProps = RouteComponentProps<
  Partial<RouteParams<'PAGE_SECTION'>>
>

export interface SectionQueriesProps
  extends SectionQueriesRouteProps,
    DocumentSidebarLayoutExtraProps {}

/**
 * Entry point for the single-Section focus view - the Section equivalent of
 * FrameQueries. The section's own identity (name, isVariantSet) is fetched
 * via GQL (getSectionsForPage, in SectionView/SectionSelector/
 * SectionsNavigationItem); this component only needs the page/version data
 * that the plain page canvas view also needs.
 */
export const SectionQueries = ({
  match,
  ...layoutProps
}: SectionQueriesProps) => {
  const { pageUUID, sectionUUID } = match.params

  const versioning = useVersioning()
  const { share, versionShortId, versionIdentifier, isViewingLatestVersion } =
    versioning

  const currentVersion = !versioning.loading ? versioning.currentVersion : null

  const { data, error } = useGetVersionQueryWithPolling({
    variables: {
      shareIdentifier: share.identifier,
      versionShortId,
    },
  })

  const subjects = useMemo(
    () => [{ type: 'PAGE' as const, permanentId: pageUUID! }],
    [pageUUID]
  )

  if (isUpgradeToLatestNeeded(error)) {
    return <UpgradeToLatestVersion error={error} />
  }

  const renderStatus = getVersionRenderStatus(data?.share?.version)
  const disableAnnotations = !share.commentsEnabled

  return (
    <CanvasScopeProvider pageUUID={pageUUID!}>
      <CanvasInteractionsProvider>
        <CanvasSelectionProvider>
          <CanvasLeftPanelResizingProvider>
            <FrameInspectorProvider>
              <GridsAndLayoutsProvider>
                <AnnotationQueryVariablesProvider
                  shareIdentifier={share.identifier}
                  versionIdentifier={versionIdentifier}
                  subjects={subjects}
                >
                  <CanvasAnnotationOverlayContext
                    disabled={disableAnnotations}
                    isViewingLatestVersion={isViewingLatestVersion}
                  >
                    <SectionView
                      layoutProps={layoutProps}
                      share={share}
                      currentVersion={currentVersion}
                      pageUUID={pageUUID!}
                      sectionUUID={sectionUUID!}
                      isViewingLatestVersion={isViewingLatestVersion}
                      renderStatus={renderStatus}
                    />
                  </CanvasAnnotationOverlayContext>
                </AnnotationQueryVariablesProvider>
              </GridsAndLayoutsProvider>
            </FrameInspectorProvider>
          </CanvasLeftPanelResizingProvider>
        </CanvasSelectionProvider>
      </CanvasInteractionsProvider>
    </CanvasScopeProvider>
  )
}
